import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar } from "@/components/AppTopBar";

const canView = (r) => r === "OWNER" || r === "CHECKER_OPERATOR" || r === "ADMIN_INVENTORY";

export default function FinalVerification() {
  const { user } = useAuth();
  const [batches, setBatches] = useState([]);
  const [batch, setBatch] = useState(null);
  const [file, setFile] = useState(null);
  const [v, setV] = useState(null);
  const [error, setError] = useState("");
  const [revocationTarget, setRevocationTarget] = useState(null);
  const [sellerReference, setSellerReference] = useState("");
  const [revocationNote, setRevocationNote] = useState("");
  const [revocationSaving, setRevocationSaving] = useState(false);

  // DA-scoped scan (replaces the old global urgent panel)
  const [scanQ, setScanQ] = useState("");
  const [scanHit, setScanHit] = useState(null);
  const [scanMsg, setScanMsg] = useState("");
  const [handsFree, setHandsFree] = useState(false);
  const [resi, setResi] = useState(null);
  const scanRef = useRef(null);

  const loadBatches = () => api.get("/checker/batches").then(({ data }) => setBatches(data)).catch(() => {});
  useEffect(() => { loadBatches(); }, []);

  const loadResi = async (bid) => {
    try { const { data } = await api.get(`/final-verifications/da/${bid}/resi`); setResi(data); return data; }
    catch { setResi({ items: [], enriched: false }); return null; }
  };
  const openBatch = async (b) => {
    setBatch(b); setV(null); setResi(null); setError(""); setScanQ(""); setScanHit(null); setScanMsg("");
    await loadResi(b.id);
    try { const { data } = await api.get(`/final-verifications/batch/${b.id}`); setV(data); } catch { /* not enriched yet */ }
  };

  const run = async (e) => {
    e.preventDefault(); setError("");
    try {
      const fd = new FormData(); fd.append("batch_id", batch.id); fd.append("file", file);
      const { data } = await api.post("/final-verifications/run", fd);
      setV(data); await loadResi(batch.id); loadBatches();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const decide = async (itemId, action) => {
    setError("");
    try { const { data } = await api.post(`/final-verifications/items/${itemId}/decision`, { action }); setV(data); await loadResi(batch.id); loadBatches(); return data; }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); return null; }
  };

  // Per-resi Gate 2 decision (DA-scoped, snapshot-optional). action: PASS | CANCEL | RETURN_CONFIRMED
  const decideResi = async (orderId, action) => {
    setError("");
    try { const { data } = await api.post(`/final-verifications/da/${batch.id}/resi/${orderId}/decision`, { action }); setResi(data); loadBatches(); try { const r = await api.get(`/final-verifications/batch/${batch.id}`); setV(r.data); } catch { /* noop */ } return data; }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); return null; }
  };

  const verifyRevocation = async (e) => {
    e.preventDefault();
    if (!revocationTarget || revocationSaving) return;
    setError(""); setRevocationSaving(true);
    try {
      const { data } = await api.get(`/final-verifications/lookup-awb?awb=${encodeURIComponent(revocationTarget.awb)}`);
      if (!data.item_id || data.source_order_id !== revocationTarget.source_order_id) {
        throw new Error("Final Check item tidak cocok dengan resi yang diverifikasi");
      }
      await api.post(`/final-verifications/items/${data.item_id}/verify-cancel-revoked`, {
        seller_center_reference: sellerReference.trim(),
        note: revocationNote.trim(),
      });
      await loadResi(batch.id); setRevocationTarget(null); setSellerReference(""); setRevocationNote("");
      setScanMsg("Persetujuan OWNER dicatat. Gate 3 dapat memproses resi setelah semua pengaman lolos.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Verifikasi gagal");
    } finally { setRevocationSaving(false); }
  };

  // DA-scoped lookup over the loaded resi list (members only — cannot resolve resi of another DA).
  const findInDA = (q) => {
    const t = q.trim().toLowerCase(); if (!t || !resi) return null;
    return resi.items.find((i) => (i.awb || "").toLowerCase() === t || (i.source_order_id || "").toLowerCase() === t) || null;
  };
  const refocusScan = () => { setScanQ(""); setScanHit(null); setTimeout(() => scanRef.current && scanRef.current.focus(), 30); };
  const isPending = (s) => !["FINAL_CLEAR", "CANCELLED"].includes(s);
  const doScan = async () => {
    setScanMsg(""); setError("");
    const q = scanQ.trim(); if (!q) return;
    const hit = findInDA(q);
    if (!hit) { setScanHit(null); setScanMsg(`Resi/Order "${q}" bukan anggota DA ini.`); if (handsFree) setTimeout(() => scanRef.current && scanRef.current.focus(), 30); return; }
    const safe = isPending(hit.gate2_status) && hit.gate2_status !== "NEEDS_REVIEW" && !hit.packed && !hit.ginee_cancel_flagged;
    if (handsFree && safe) {
      const upd = await decideResi(hit.order_id, "PASS");
      if (upd) { setScanMsg(`✓ ${hit.source_order_id} FINAL_CLEAR (auto) — siap Paket Keluar. Scan berikutnya.`); refocusScan(); } else setScanHit(hit);
      return;
    }
    setScanHit(hit);
    if (handsFree && !safe) setScanMsg(`Berhenti (perlu keputusan manual): ${hit.packed ? "sudah Paket Keluar" : "status " + hit.gate2_status}.`);
  };
  const scanDecide = async (action) => {
    if (!scanHit) return;
    const upd = await decideResi(scanHit.order_id, action);
    if (upd) { setScanMsg(action === "PASS" ? "Resi di-FINAL_CLEAR — siap Paket Keluar." : action === "CANCEL" ? "Resi di-Batalkan (recovery)." : "Paket ditemukan."); if (handsFree) refocusScan(); else { const r = (upd.items || []).find((i) => i.order_id === scanHit.order_id); setScanHit(r || null); } }
  };

  if (user && !canView(user.role)) {
    return (<div className="app-shell"><AppTopBar active="final" /><main className="content"><div className="alert-error" data-testid="access-denied">Akses ditolak.</div></main></div>);
  }

  const exceptions = v ? v.items.filter((i) => i.decision_status !== "FINAL_CLEAR" && i.decision_status !== "CANCELLED") : [];
  const recover = v ? v.items.filter((i) => i.decision_status === "PACKAGE_TO_RECOVER") : [];
  const cleared = v ? v.items.filter((i) => i.stock_out && i.stock_out.status === "STOCKED_OUT") : [];
  const stockedOut = cleared.length;

  const scanStockedOut = scanHit && scanHit.packed;
  const scanClear = scanHit && scanHit.gate2_status === "FINAL_CLEAR";

  return (
    <div className="app-shell">
      <AppTopBar active="final" />
      <main className="content">
        {error && <div className="alert-error" data-testid="error-banner">{error}</div>}

        {/* STEP 1 — pick a DA (OPEN or CLOSED) */}
        {!batch && (
          <section className="panel" data-testid="final-batch-panel">
            <h2 className="panel-title">Final Check — Pilih DA</h2>
            <p className="hint" style={{ marginTop: -6 }}>Pilih DA (OPEN atau CLOSED) untuk mulai. Gate 2 diputuskan per-resi di dalam DA; DA tidak perlu ditutup.</p>
            <div className="table-scroll">
              <table className="data-table" data-testid="final-batch-table">
                <thead><tr><th>DA KE</th><th>Work Date</th><th>Status</th><th>Total Resi</th><th>Gate 2 Done</th><th>Gate 2 Pending</th><th>Aksi</th></tr></thead>
                <tbody>
                  {batches.length === 0 && <tr><td colSpan={7} className="empty-cell">Belum ada DA.</td></tr>}
                  {batches.map((b) => (
                    <tr key={b.id} data-testid={`final-batch-row-${b.batch_code}`}>
                      <td className="mono">DA KE {b.sequence_no}</td>
                      <td>{b.work_date}</td>
                      <td><span className={`status-pill ${b.status === "OPEN" ? "on" : ""}`} data-testid={`final-da-status-${b.batch_code}`}>{b.status}</span></td>
                      <td className="mono" data-testid={`final-da-total-${b.batch_code}`}>{b.total}</td>
                      <td className="mono" data-testid={`final-da-g2done-${b.batch_code}`}>{b.gate2_done ?? 0}</td>
                      <td className="mono" data-testid={`final-da-g2pending-${b.batch_code}`}>{b.gate2_pending ?? 0}</td>
                      <td><button className="btn-mini" onClick={() => openBatch(b)} data-testid={`final-check-${b.batch_code}`}>Pilih DA</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* STEP 2 — inside a DA */}
        {batch && (
          <>
            <section className="panel" data-testid="final-upload-panel">
              <div className="panel-head">
                <h2 className="panel-title">DA KE {batch.sequence_no} — {batch.work_date} <span className={`status-pill ${batch.status === "OPEN" ? "on" : ""}`} style={{ marginLeft: 8 }}>{batch.status}</span></h2>
                <button className="btn-ghost" onClick={() => { setBatch(null); setV(null); }} data-testid="final-back">Kembali</button>
              </div>
              <form onSubmit={run}>
                <label className="field-label">Snapshot Ginee (.xlsx) — OPSIONAL: enrichment cancel/missing/source-conflict. Gate 2 per-resi TIDAK menunggu ini.</label>
                <input type="file" accept=".xlsx,.xls" className="field-input" onChange={(e) => setFile(e.target.files[0])} data-testid="final-file-input" />
                <div className="form-actions"><button className="btn-primary" type="submit" disabled={!file} data-testid="final-run-button">Enrich dengan Snapshot Ginee</button>
                  <span className="hint" style={{ marginLeft: 10 }} data-testid="final-enrich-status">{resi?.enriched ? "Status: sudah di-enrich Ginee" : "Status: belum di-enrich"}</span></div>
              </form>
            </section>

            {resi && (<>
              {/* DA-scoped scan/search — members of THIS DA only; snapshot NOT required */}
              <section className="panel" data-testid="final-scan-panel">
                <h2 className="panel-title">Scan / Cari Resi di DA Ini</h2>
                <p className="hint" style={{ marginTop: -6 }}>Hanya resi anggota DA KE {batch.sequence_no} yang bisa ditemukan. Keputusan Gate 2 per-resi; FINAL_CLEAR boleh lanjut Paket Keluar walau DA masih OPEN.</p>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 260px", minWidth: 240 }}>
                    <label className="field-label">No. Resi / AWB (atau ID Pesanan)</label>
                    <input ref={scanRef} className="field-input" value={scanQ} onChange={(e) => setScanQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doScan()} placeholder="scan / ketik AWB" data-testid="final-scan-input" />
                  </div>
                  <button className="btn-primary" onClick={doScan} style={{ width: "auto" }} data-testid="final-scan-btn">Cari</button>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                    <input type="checkbox" checked={handsFree} onChange={(e) => setHandsFree(e.target.checked)} data-testid="final-handsfree-toggle" />
                    HANDS-FREE / AUTO LANJUT JIKA AMAN
                  </label>
                </div>
                {scanMsg && <div className="hint" style={{ marginTop: 8, fontWeight: 700 }} data-testid="final-scan-msg">{scanMsg}</div>}
                {scanHit && (
                  <div style={{ marginTop: 12 }} data-testid="final-scan-result">
                    <div>Order <b>{scanHit.source_order_id}</b> — status <span className="tag-warn">{scanHit.gate2_status}</span>{scanStockedOut && <span className="status-pill on" style={{ marginLeft: 6 }}>PAKET KELUAR</span>}{scanClear && !scanStockedOut && <span className="status-pill on" style={{ marginLeft: 6 }}>FINAL_CLEAR</span>}</div>
                    <div className="hint" style={{ margin: "4px 0" }}>{(scanHit.items || []).map((x) => `${x.sku}×${x.qty}`).join(", ")}</div>
                    <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                      <button className="btn-mini" disabled={scanHit.packed || scanHit.gate2_status === "FINAL_CLEAR"} onClick={() => scanDecide("PASS")} data-testid="final-scan-keep">Lanjut (FINAL_CLEAR)</button>
                      <button className="btn-mini danger" disabled={scanHit.packed || ["CANCELLED", "PACKAGE_TO_RECOVER"].includes(scanHit.gate2_status)} onClick={() => scanDecide("CANCEL")} data-testid="final-scan-cancel">Batalkan</button>
                      {user?.role === "OWNER" && scanHit.ginee_cancel_flagged && scanClear && !scanHit.cancel_revocation_verified && !scanHit.packed && <button className="btn-mini" onClick={() => setRevocationTarget(scanHit)} data-testid="final-scan-revoke-verify">Verifikasi Cancel Dicabut</button>}
                       {scanHit.gate2_status === "PACKAGE_TO_RECOVER" && <button className="btn-mini" onClick={() => scanDecide("RETURN_CONFIRMED")} data-testid="final-scan-recover">Paket Ditemukan</button>}
                    </div>
                  </div>
                )}
              </section>

              {revocationTarget && <section className="panel" data-testid="cancel-revocation-verification">
                <h3>Verifikasi Pembatalan Dicabut — OWNER</h3>
                <p className="hint">Periksa Seller Center terlebih dahulu. Tanda cancel Ginee tetap tersimpan. Persetujuan hanya berlaku untuk order ini dan dicatat sebagai audit.</p>
                <p><b>Order:</b> {revocationTarget.source_order_id} — <b>Resi:</b> {revocationTarget.awb}</p>
                <form onSubmit={verifyRevocation}>
                  <label className="field-label">Referensi bukti Seller Center</label>
                  <input className="form-control" value={sellerReference} onChange={(e) => setSellerReference(e.target.value)} required minLength={6} maxLength={255} placeholder="Nomor bukti, tautan order, atau referensi verifikasi" />
                  <label className="field-label">Catatan verifikasi</label>
                  <textarea className="form-control" value={revocationNote} onChange={(e) => setRevocationNote(e.target.value)} required minLength={12} placeholder="Customer mencabut pembatalan; status Seller Center aktif dan boleh dikirim" />
                  <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button type="submit" className="btn-mini" disabled={revocationSaving || sellerReference.trim().length < 6 || revocationNote.trim().length < 12}> {revocationSaving ? "Memverifikasi…" : "Sahkan Pemulihan"}</button>
                    <button type="button" className="btn-mini" onClick={() => setRevocationTarget(null)} disabled={revocationSaving}>Batal</button>
                  </div>
                </form>
              </section>}

              <section className="panel" data-testid="final-resi-panel">
                <h2 className="panel-title">Resi DA — Checker Complete ({resi.items.length})</h2>
                <p className="hint" style={{ marginTop: -6 }}>Gate 2 per-resi. Snapshot Ginee opsional; belum di-enrich ≠ tidak bisa diputuskan.</p>
                <div className="table-scroll">
                  <table className="data-table" data-testid="final-resi-table">
                    <thead><tr><th>ID Pesanan</th><th>AWB</th><th>SKU/Qty</th><th>Gate 2</th><th>Ginee</th><th>Aksi</th></tr></thead>
                    <tbody>
                      {resi.items.length === 0 && <tr><td colSpan={6} className="empty-cell">Belum ada resi Checker-complete.</td></tr>}
                      {resi.items.map((r) => (
                        <tr key={r.order_id} data-testid={`resi-row-${r.source_order_id}`}>
                          <td className="mono">{r.source_order_id}</td>
                          <td className="mono">{r.awb || "-"}</td>
                          <td>{(r.items || []).map((x) => `${x.sku}×${x.qty}`).join(", ")}</td>
                          <td>{r.packed ? <span className="status-pill on">PAKET KELUAR</span> : <span className={`status-pill ${r.gate2_status === "FINAL_CLEAR" ? "on" : "warn"}`} data-testid={`resi-gate2-${r.source_order_id}`}>{r.gate2_status}</span>}</td>
                          <td>{r.ginee_cancel_flagged ? <span className="tag-warn">Ginee: Cancel</span> : r.enriched ? <span className="status-pill on">enriched</span> : <span className="hint">belum</span>}{r.cancel_revocation_verified && <span className="status-pill on">Dicabut — Terverifikasi</span>}</td>
                          <td>
                            {!r.packed && r.gate2_status !== "FINAL_CLEAR" && <button className="btn-mini" onClick={() => decideResi(r.order_id, "PASS")} data-testid={`resi-pass-${r.source_order_id}`}>Lanjut</button>}
                            {!r.packed && !["CANCELLED", "PACKAGE_TO_RECOVER"].includes(r.gate2_status) && <button className="btn-mini danger" onClick={() => decideResi(r.order_id, "CANCEL")} data-testid={`resi-cancel-${r.source_order_id}`}>Batalkan</button>}
                            {user?.role === "OWNER" && r.ginee_cancel_flagged && r.gate2_status === "FINAL_CLEAR" && !r.cancel_revocation_verified && !r.packed && <button className="btn-mini" onClick={() => setRevocationTarget(r)} data-testid={`resi-verify-revocation-${r.source_order_id}`}>Verifikasi Cancel Dicabut</button>}
                             {r.gate2_status === "PACKAGE_TO_RECOVER" && <button className="btn-mini" onClick={() => decideResi(r.order_id, "RETURN_CONFIRMED")} data-testid={`resi-recover-${r.source_order_id}`}>Paket Ditemukan</button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>)}

            {v && (
              <>
                <section className="panel" data-testid="final-summary-panel">
                  <div className="panel-head"><h2 className="panel-title">Hasil Final Verification</h2>
                    <span className={`status-pill ${v.status === "FINAL_VERIFIED" ? "on" : "off"}`} data-testid="final-status">{v.status}</span></div>
                  <div className="stat-grid">
                    <div className="stat-box"><span className="meta-label">Total Batch</span><span className="stat-value" data-testid="fv-total">{v.total_batch_orders}</span></div>
                    <div className="stat-box"><span className="meta-label">Matched</span><span className="stat-value" data-testid="fv-matched">{v.matched_orders}</span></div>
                    <div className="stat-box"><span className="meta-label">Final Clear</span><span className="stat-value" data-testid="fv-clear">{v.final_clear_count}</span></div>
                    <div className="stat-box"><span className="meta-label">Perlu Review</span><span className="stat-value" data-testid="fv-flagged">{v.cancel_flagged_count}</span></div>
                    <div className="stat-box"><span className="meta-label">Missing</span><span className="stat-value" data-testid="fv-missing">{v.missing_order_count}</span></div>
                    <div className="stat-box"><span className="meta-label">Extra</span><span className="stat-value" data-testid="fv-extra">{v.extra_order_count}</span></div>
                    <div className="stat-box"><span className="meta-label">Stok Keluar</span><span className="stat-value" data-testid="fv-stocked-out">{stockedOut}</span></div>
                    <div className="stat-box"><span className="meta-label">Stok Kurang</span><span className="stat-value" data-testid="fv-stock-short" style={{ color: v.stock_short_count > 0 ? "#E07171" : undefined }}>{v.stock_short_count ?? 0}</span></div>
                  </div>
                </section>

                <section className="panel" data-testid="final-exceptions-panel">
                  <h2 className="panel-title">{exceptions.length} Order Perlu Review</h2>
                  <div className="table-scroll">
                    <table className="data-table" data-testid="final-exceptions-table">
                      <thead><tr><th>ID Pesanan</th><th>AWB</th><th>Cancellation</th><th>SKU/Qty</th><th>Stok</th><th>Status</th><th>Aksi</th></tr></thead>
                      <tbody>
                        {exceptions.length === 0 && <tr><td colSpan={7} className="empty-cell">Tidak ada exception.</td></tr>}
                        {exceptions.map((i) => (
                          <tr key={i.id} data-testid={`fv-item-${i.source_order_id}`}>
                            <td className="mono">{i.source_order_id}</td>
                            <td className="mono">{i.awb || "-"}</td>
                            <td>{i.cancellation_reason_source || i.cancellation_time_source || "-"}</td>
                            <td>{i.items.map((x) => `${x.sku}×${x.qty}`).join(", ")}</td>
                            <td data-testid={`fv-stockcell-${i.source_order_id}`}>
                              {i.stock_out && i.stock_out.status === "STOCKED_OUT"
                                ? <span className="status-pill on">Stok Keluar</span>
                                : i.decision_status === "FINAL_CLEAR_STOCK_SHORT" ? <span className="tag-warn">Stok Kurang</span> : "-"}
                            </td>
                            <td><span className="tag-warn">{i.decision_status}</span></td>
                            <td>
                              {(i.decision_status === "CANCEL_FLAGGED" || i.decision_status === "MISSING_IN_FINAL_SNAPSHOT") && (<>
                                <button className="btn-mini" onClick={() => decide(i.id, "KEEP_ACTIVE")} data-testid={`keep-${i.source_order_id}`}>Keep Active</button>
                                <button className="btn-mini danger" onClick={() => decide(i.id, "CONFIRM_CANCEL")} data-testid={`cancel-${i.source_order_id}`}>Confirm Cancel</button>
                              </>)}
                              {i.decision_status === "PACKAGE_TO_RECOVER" && (
                                <button className="btn-mini" onClick={() => decide(i.id, "RETURN_CONFIRMED")} data-testid={`recover-${i.source_order_id}`}>Paket Ditemukan</button>)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                {cleared.length > 0 && (
                  <section className="panel" data-testid="final-cleared-panel">
                    <h2 className="panel-title">Stok Keluar — Final Clear ({cleared.length})</h2>
                    <div className="table-scroll">
                      <table className="data-table" data-testid="final-cleared-table">
                        <thead><tr><th>ID Pesanan</th><th>AWB</th><th>SKU/Qty</th><th>Stok</th></tr></thead>
                        <tbody>
                          {cleared.map((i) => (
                            <tr key={i.id} data-testid={`fv-cleared-${i.source_order_id}`}>
                              <td className="mono">{i.source_order_id}</td><td className="mono">{i.awb || "-"}</td>
                              <td>{i.items.map((x) => `${x.sku}×${x.qty}`).join(", ")}</td>
                              <td><span className="status-pill on" data-testid={`fv-cleared-badge-${i.source_order_id}`}>Stok Keluar</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {recover.length > 0 && (
                  <section className="panel" data-testid="final-recovery-panel">
                    <h2 className="panel-title">Paket Harus Dicari ({recover.length})</h2>
                    <div className="table-scroll">
                      <table className="data-table">
                        <thead><tr><th>ID Pesanan</th><th>AWB</th><th>SKU/Qty</th><th>Aksi</th></tr></thead>
                        <tbody>
                          {recover.map((i) => (
                            <tr key={i.id}>
                              <td className="mono">{i.source_order_id}</td><td className="mono">{i.awb || "-"}</td>
                              <td>{i.items.map((x) => `${x.sku}×${x.qty}`).join(", ")}</td>
                              <td><button className="btn-mini" onClick={() => decide(i.id, "RETURN_CONFIRMED")} data-testid={`found-${i.source_order_id}`}>Paket Ditemukan</button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
